import { useLayoutEffect, useRef } from "react";

function useResizeObserver<T extends HTMLElement>(callback: (target: T, entry: ResizeObserverEntry) => void) {
  const ref = useRef<T>(null);
  const frameRef = useRef<number | null>(null);
  const lastSizeRef = useRef<{ width: number; height: number } | null>(null);

  useLayoutEffect(() => {
    const element = ref?.current;

    if (!element) return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;

      const nextWidth = entry.contentRect.width;
      const nextHeight = entry.contentRect.height;
      const lastSize = lastSizeRef.current;

      if (lastSize && lastSize.width === nextWidth && lastSize.height === nextHeight) {
        return;
      }

      lastSizeRef.current = { width: nextWidth, height: nextHeight };

      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
      }

      frameRef.current = requestAnimationFrame(() => {
        callback(element, entry);
      });
    });

    lastSizeRef.current = null; // Reset lastSizeRef to ensure callback runs for new elements
    observer.observe(element);
    return () => {
      observer.disconnect();
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
    };
  }, [callback, ref]);

  return ref;
}

export default useResizeObserver;
